import i18n from 'i18next';
// Helper functions
// Components
import {
  Button,
  CollapseCard,
  GridItem,
  notify,
  PendingContent,
  Spinner,
  Table,
} from 'plume-ui';
import { CollapseCardStyles } from 'plume-ui/dist/components/Card/CollapseCard';
import { DataRow } from 'plume-ui/dist/components/Table/Table';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DependencyContainer } from '../../../../DependencyContainer';
import { useFetchData } from '../../../../hooks/useFetchData';
import { MixPanelEvents } from '../../../../mixPanelEvents';
import {
  activePartnerAtom,
  configurationInfoModalAtom,
  urlModalAtom,
} from '../../../../state';
import { ConfigurationInfoModalData, Maybe } from '../../../../types';
import FormattedMessage from '../../../../utils/components/FormattedMessage'; // Hooks
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import { ConfigurationCTAUrlResponse } from '../../../Monitor/types';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { EmptyUrlObject } from './fixtures';
import { parseUrlToDataRows, urlListTableHeaders } from './helpers';

type UrlsProps = {
  editAllowed: boolean;
};

type UrlsStyles = {};

const { outreachService } = new DependencyContainer();

const Urls: AdjustableComponent<UrlsProps, UrlsStyles> = ({ editAllowed }) => {
  const [tableData, setTableData] = useState<DataRow[]>([]);
  const setModal = useSetRecoilState(urlModalAtom);
  const setInfoModal = useSetRecoilState(configurationInfoModalAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();

  const [
    loadingUrls,
    urlList,
    urlsLoadingError,
    runFetchUrlsList,
  ] = useFetchData<ConfigurationCTAUrlResponse[], any>(
    async () => {
      if (!activePartner?.id) return;
      const listItems = await outreachService.getCTAUrls(activePartner.id);
      return listItems;
    },
    undefined,
    [activePartner],
    () => activePartner === undefined,
  );

  const getUrlById = (id: string) => urlList?.find((url) => url.id === id);

  useEffect(() => {
    if (urlList === undefined) return;
    setTableData(parseUrlToDataRows(urlList));
  }, [loadingUrls]);

  const handleAddNew = () => {
    setModal({
      isOpen: true,
      type: 'add',
      url: EmptyUrlObject,
      refetchUrls: runFetchUrlsList,
    });
  };

  const handleEdit = (id: string) => {
    setModal({
      isOpen: true,
      type: 'edit',
      url: getUrlById(id) || EmptyUrlObject,
      refetchUrls: runFetchUrlsList,
    });
  };

  const handleTest = async (urlLink: string) => {
    window.open(urlLink.includes('http') ? urlLink : `https://${urlLink}`);
  };

  const notifyDeleteError = () => {
    notify({
      title: t('configuration.notifications.error.url.delete.title'),
      body: t('configuration.notifications.error.url.delete.body'),
      type: 'error',
    });
  };

  const handleDelete = async (id: string) => {
    if (!activePartner?.id) return;
    await outreachService
      .deleteCTAUrl(activePartner.id, id)
      .then(() => {
        runFetchUrlsList();
        notify({
          title: t('configuration.notifications.success.url.delete.title'),
          body: t('configuration.notifications.success.url.delete.body'),
          type: 'success',
        });
        trackEvent({
          eventName: MixPanelEvents.URL_SUCCESS,
          additionalContent: {
            INTERACTION: 'DELETE',
            URL_ID: id,
          },
        });
      })
      .catch((error) => {
        notifyDeleteError();
        trackEvent({
          eventName: MixPanelEvents.URL_FAILURE,
          additionalContent: {
            INTERACTION: 'DELETE',
            URL_ID: id,
            ERROR_MESSAGE: error?.response?.data?.message || 'no message',
          },
        });
      });
  };

  return (
    <GridItem colSpan="12" tabletColSpan="12">
      <CollapseCard
        title={
          <div className="Configuration__collapseCard__title">
            <FormattedMessage id="configuration.URLs.title" />
          </div>
        }
        subtitle={i18n.t('configuration.URLs.subtitle')}
        classes={(current: CollapseCardStyles) => ({
          ...current,
          root: `${current.root} Configuration__collapseCard`,
          content: `${current.content} Configuration__collapseCard__content`,
        })}
      >
        <PendingContent
          loading={loadingUrls && !urlsLoadingError}
          loader={Spinner}
        >
          {urlList !== undefined && Boolean(tableData.length) && (
            <div className="Configuration__urls">
              <Table
                truncateCellContent
                headerRow={urlListTableHeaders({
                  urlList: urlList || [],
                  openOutreachListModal: (
                    data: Maybe<ConfigurationInfoModalData>,
                  ) => {
                    setInfoModal({
                      isOpen: true,
                      type: 'outreachList',
                      data,
                    });
                  },
                  editUrl: handleEdit,
                  deleteUrl: handleDelete,
                  editAllowed,
                  testUrl: handleTest,
                })}
                dataRows={tableData}
              />
            </div>
          )}
        </PendingContent>
        <Button
          styleVariant="primary"
          onClick={handleAddNew}
          disabled={!editAllowed}
        >
          <FormattedMessage id="configuration.actions.addNew" />
        </Button>
      </CollapseCard>
    </GridItem>
  );
};

export default memo(Urls);
